import { useEffect, useRef } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";
import "@geoman-io/leaflet-geoman-free";
import "@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css";

/**
 * OSM-style building outline drawing via leaflet-geoman (react-leaflet v4).
 *
 * Render this as a child of <MapContainer>. When `active` is true it enables
 * polygon draw/edit/remove controls and reports the current outline as a GeoJSON
 * geometry string through `onChange`. An existing `polygon` (GeoJSON geometry,
 * lng/lat order) is pre-loaded as an editable layer so the user can modify it.
 *
 * Props:
 *   active   : boolean  - show controls / enable drawing
 *   polygon  : object|null - existing GeoJSON geometry (Polygon/MultiPolygon)
 *   onChange : (geojsonString|null) => void
 */
export const BuildingDraw = ({ active, polygon, onChange }) => {
  const map = useMap();
  const layerRef = useRef(null);

  // Emit the current layer's geometry (or null) to the parent.
  const emit = () => {
    const layer = layerRef.current;
    if (!layer) return onChange && onChange(null);
    const gj = layer.toGeoJSON();
    const geom = gj.type === "Feature" ? gj.geometry : gj;
    onChange && onChange(JSON.stringify(geom));
  };

  // Toggle geoman controls + draw events when `active` changes.
  useEffect(() => {
    if (!map || !map.pm) return;

    if (!active) {
      map.pm.removeControls();
      map.pm.disableDraw();
      return;
    }

    map.pm.addControls({
      position: "topright",
      drawMarker: false,
      drawCircle: false,
      drawCircleMarker: false,
      drawPolyline: false,
      drawRectangle: true,
      drawPolygon: true,
      drawText: false,
      cutPolygon: false,
      rotateMode: false,
      editMode: true,
      dragMode: true,
      removalMode: true,
    });

    // New shape drawn -> keep only the latest as the building's outline.
    const onCreate = (e) => {
      if (layerRef.current && layerRef.current !== e.layer) {
        try { map.removeLayer(layerRef.current); } catch (_) {}
      }
      layerRef.current = e.layer;
      e.layer.on("pm:edit", emit);
      emit();
    };
    const onRemove = (e) => {
      if (e.layer === layerRef.current) {
        layerRef.current = null;
        emit();
      }
    };

    map.on("pm:create", onCreate);
    map.on("pm:remove", onRemove);

    return () => {
      map.off("pm:create", onCreate);
      map.off("pm:remove", onRemove);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, map]);

  // Pre-load an existing polygon as an editable layer when entering draw mode.
  useEffect(() => {
    if (!map || !active) return;
    if (layerRef.current) return; // already have a working layer
    if (!polygon) return;
    try {
      const layer = L.geoJSON({ type: "Feature", geometry: polygon }).getLayers()[0];
      if (layer) {
        layer.addTo(map);
        layer.pm && layer.pm.enable({ allowSelfIntersection: false });
        layer.on("pm:edit", emit);
        layerRef.current = layer;
      }
    } catch (_) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, polygon, map]);

  return null;
};

export default BuildingDraw;
